import { HUB_ROUTE_PATTERNS, HubPageName } from '../constants';

/**
 * Resolves a pathname to a standardized HubPageName.
 * Uses pattern matching with fallback to UNKNOWN for unrecognized routes.
 *
 * @param pathname - The URL pathname (e.g., '/overview/12345')
 * @returns The standardized page name (e.g., 'hub_program_dashboard')
 *
 * @example
 * getHubPageName('/overview') // Returns HubPageName.OVERVIEW
 * getHubPageName('/overview/12345') // Returns HubPageName.PROGRAM_DASHBOARD
 * getHubPageName('/contests/abc123') // Returns HubPageName.CONTEST_DETAILS
 */
export const getHubPageName = (pathname: string): HubPageName => {
  // Normalize pathname: remove trailing slash if present (except for root)
  const normalizedPath =
    pathname.endsWith('/') && pathname.length > 1
      ? pathname.slice(0, -1)
      : pathname;

  // Find matching pattern (patterns are ordered by specificity)
  for (const route of HUB_ROUTE_PATTERNS) {
    if (route.pattern.test(normalizedPath)) {
      return route.pageName;
    }
  }

  // Fallback for unknown routes
  return HubPageName.UNKNOWN;
};

/**
 * List of page names that represent aggregated routes.
 * These are routes with dynamic segments that should be tracked as a single event
 * (e.g., /overview/123 and /overview/456 both become hub_program_dashboard).
 */
const AGGREGATED_PAGE_NAMES: HubPageName[] = [
  HubPageName.PROGRAM_DASHBOARD,
  HubPageName.CONTEST_DETAILS,
  HubPageName.CONTEST_DASHBOARD,
  HubPageName.TRADE_TALK_ARTICLE,
  HubPageName.PLAYBOOK_DETAIL,
  HubPageName.PARTNERS,
  HubPageName.POSITION,
  HubPageName.AFTER_PURCHASE,
  HubPageName.NEW_ADDON,
  HubPageName.PAYSAFE_CHECKOUT,
];

/**
 * Checks if a page name represents an aggregated route.
 * Aggregated routes have dynamic segments that are stripped for analytics.
 *
 * @param pageName - The HubPageName to check
 * @returns true if this is an aggregated page name
 */
export const isAggregatedPageName = (pageName: HubPageName): boolean => {
  return AGGREGATED_PAGE_NAMES.includes(pageName);
};
